<script setup lang="ts">
import { computed, useSlots } from 'vue';

import AppSvgIcon from '../appSvgIcon/AppSvgIcon.vue';

import type {
  AppNotificationDescription,
  AppNotificationIcon,
  AppNotificationTheme,
  AppNotificationTitle,
} from './AppNotification.model';

interface AppNotificationProps {
  theme: AppNotificationTheme;
  description?: AppNotificationDescription;
  icon?: AppNotificationIcon;
  forceWhiteBackground?: boolean;
  title?: AppNotificationTitle;
  alignCenter?: boolean;
}

const props = defineProps<AppNotificationProps>();

const slots = useSlots();
const ACTION_SLOT_NAME = 'actions';

const hasActionSlot = computed(() => !!slots[ACTION_SLOT_NAME]);
</script>

<template>
  <div
    :class="[
      'app-notification',
      `${theme}`,
      {
        'white-background': props.forceWhiteBackground,
        'align-center': props.alignCenter,
      },
    ]"
  >
    <AppSvgIcon v-if="props.icon" :name="props.icon.name" size="24" />
    <div class="content">
      <div class="text">
        <span
          v-if="props.title"
          :class="['title', { large: props.title.size }]"
        >
          {{ props.title.text }}
        </span>
        <span v-if="props.description" class="description">
          {{ props.description.text }}
        </span>
      </div>
      <div v-if="hasActionSlot" class="action">
        <slot name="actions" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '../../styles/fonts' as *;

.app-notification {
  padding: var(--spacing-16) var(--spacing-12);
  border-radius: var(--border-radius-4);
  color: var(--color-neutral-70);
  display: flex;
  gap: var(--spacing-12);

  &.sembo {
    background-color: var(--color-information-light);
    border-left: 5px solid var(--color-information-dark);
  }

  &.neutral {
    background-color: var(--color-neutral-light);
    border-left: 5px solid var(--color-neutral-dark);
  }

  &.success {
    background-color: var(--color-success-light);
    border-left: 5px solid var(--color-success-dark);
  }

  &.warning {
    background-color: var(--color-warning-light);
    border-left: 5px solid var(--color-warning-dark);
  }

  &.error {
    background-color: var(--color-error-light);
    border-left: 5px solid var(--color-error-dark);
  }

  &.align-center {
    align-items: center;
  }

  &.white-background {
    background-color: var(--color-white);
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-12);

    .text {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-4);

      .title {
        @include font-label-m-bold;

        &.large {
          @include font-label-l-bold;
        }
      }

      .description {
        @include font-body-m-light;
      }
    }
    .action {
      @include font-link-s-light;
    }
  }
}
</style>
